/**
 * Reusable ConfidenceBadge component with tooltip support
 * Displays confidence level with context-specific explanations
 * Supports both hover (desktop) and tap (mobile) interactions
 */

import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { ShieldCheck, CircleDot, AlertTriangle, Scale, Users, UserX, HelpCircle } from 'lucide-react';

type ConfidenceContext = 
  | 'coordinates'     // Geographic coordinate accuracy
  | 'dating'          // Archaeological/historical dating
  | 'event'           // Historical event occurrence
  | 'viewpoint'       // Scholarly consensus level
  | 'general';        // Generic confidence

interface ConfidenceBadgeProps {
  confidence: Confidence | string; // Allow string for viewpoint confidence values
  context?: ConfidenceContext;
  className?: string;
}

const TOOLTIP_TEXT: Record<ConfidenceContext, string> = {
  coordinates: 'How accurately we know the location',
  dating: 'How certain we are about the date',
  event: 'How well-attested this event is historically',
  viewpoint: 'Level of scholarly consensus',
  general: 'Level of certainty or consensus'
};

const CONFIDENCE_LABELS: Record<Confidence|string, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  disputed: 'Disputed',
  'very-low': 'Very Low',
  majority: 'Majority',
  minority: 'Minority',
  uncertain: 'Uncertain'
};

const CONFIDENCE_ICONS = {
  high: ShieldCheck,
  medium: CircleDot,
  low: AlertTriangle,
  disputed: Scale,
  'very-low': HelpCircle,
  majority: Users,
  minority: UserX,
  uncertain: HelpCircle
};

const CONFIDENCE_DESCRIPTIONS: Record<string, Record<ConfidenceContext, string>> = {
  high: {
    coordinates: 'Universally accepted identification',
    dating: 'Universally accepted dating with scholarly consensus',
    event: 'Well-attested with strong historical evidence',
    viewpoint: 'Strong scholarly consensus',
    general: 'High degree of certainty'
  },
  medium: {
    coordinates: 'Generally accepted with some debate',
    dating: 'Generally accepted with some scholarly debate',
    event: 'Reasonably attested with some debate',
    viewpoint: 'Moderate scholarly consensus',
    general: 'Moderate degree of certainty'
  },
  low: {
    coordinates: 'Multiple proposed locations, uncertain',
    dating: 'Uncertain, multiple possibilities',
    event: 'Limited historical evidence',
    viewpoint: 'Limited scholarly consensus',
    general: 'Low degree of certainty'
  },
  disputed: {
    coordinates: 'Active scholarly debate with competing theories',
    dating: 'Active scholarly debate with well-supported alternatives',
    event: 'Significant scholarly debate',
    viewpoint: 'Significant scholarly disagreement',
    general: 'Actively disputed'
  },
  'very-low': {
    coordinates: 'Highly uncertain or speculative',
    dating: 'Highly uncertain',
    event: 'Highly uncertain',
    viewpoint: 'Highly uncertain',
    general: 'Very low certainty'
  },
  majority: {
    coordinates: 'Held by most scholars',
    dating: 'Held by most scholars',
    event: 'Held by most scholars',
    viewpoint: 'Held by most scholars',
    general: 'Majority view'
  },
  minority: {
    coordinates: 'Held by a minority of scholars',
    dating: 'Held by a minority of scholars',
    event: 'Held by a minority of scholars',
    viewpoint: 'Held by a minority of scholars',
    general: 'Minority view'
  },
  uncertain: {
    coordinates: 'No clear scholarly consensus',
    dating: 'No clear scholarly consensus',
    event: 'No clear scholarly consensus',
    viewpoint: 'No clear scholarly consensus',
    general: 'Uncertain'
  }
};

export function ConfidenceBadge({ confidence, context = 'general', className = '' }: ConfidenceBadgeProps) {
  const [open, setOpen] = useState(false);
  const tooltipText = TOOLTIP_TEXT[context];
  const description = CONFIDENCE_DESCRIPTIONS[confidence]?.[context];
  const label = CONFIDENCE_LABELS[confidence] || confidence;
  const Icon = CONFIDENCE_ICONS[confidence as keyof typeof CONFIDENCE_ICONS] || HelpCircle;

  // Determine badge styling based on confidence level and context
  let variant: 'default' | 'outline' | 'secondary' = 'outline';
  let badgeClassName = 'text-xs';

  // Context-specific styling
  if (context === 'coordinates') {
    // Coordinates: green (high), yellow (medium), orange (low), red (very-low)
    if (confidence === 'high') {
      variant = 'default';
      badgeClassName = 'text-xs bg-green-500';
    } else if (confidence === 'medium') {
      badgeClassName = 'text-xs border-yellow-400 text-yellow-600';
    } else if (confidence === 'low') {
      badgeClassName = 'text-xs border-orange-400 text-orange-600';
    } else {
      badgeClassName = 'text-xs border-red-400 text-red-600';
    }
  } else if (context === 'event') {
    // Events: green (high), yellow (medium), gray (low)
    if (confidence === 'high') {
      variant = 'default';
      badgeClassName = 'text-xs bg-green-500';
    } else if (confidence === 'medium') {
      badgeClassName = 'text-xs border-yellow-400 text-yellow-600';
    } else {
      badgeClassName = 'text-xs border-gray-400 text-gray-600';
    }
  } else if (context === 'viewpoint') {
    // Scholarly viewpoints: different variants
    if (confidence === 'majority') {
      variant = 'default';
    } else if (confidence === 'minority') {
      variant = 'secondary';
    } else {
      variant = 'outline';
    }
    badgeClassName = 'text-xs';
  } else {
    // Dating and general: high gets default variant, others get outline
    if (confidence === 'high') {
      variant = 'default';
    }
    badgeClassName = 'text-xs';
  }

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <Badge
          variant={variant}
          className={`${badgeClassName} ${className} cursor-help flex items-center gap-1`}
          onClick={() => setOpen(!open)}
        >
          <Icon size={12} />
          Confidence: {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-1">
          <p className="font-semibold">{tooltipText}</p>
          {description && <p className="text-xs opacity-90">{description}</p>}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
